import type { ReactNode } from 'react';
import { Button, Typography } from '@mui/material';

import {
  IntakeJourneyShell,
  type IntakeJourneyStage,
  type IntakeJourneyStageState,
} from './IntakeJourneyShell';
import { IntakeJourneyStatus } from './IntakeJourneyStatus';
import { IntakeKickoffCard } from './IntakeKickoffCard';
import { IntakeVerificationStatus } from './IntakeVerificationStatus';

export interface IntakeJourneyProgress {
  /** Agreements & setup counts as done. Until signing exists this is "services saved". */
  agreementsDone: boolean;
  agreementsCaption: string;
  /** Current provider's intake percent complete, from the live form. */
  intakePercent: number;
}

const LOCKED_AFTER_AGREEMENTS = 'Opens after agreements.';

/** The design's lock ladder, from the signals we actually have. Staff embeds pass no progress. */
export function journeyStagesFromProgress(
  progress: IntakeJourneyProgress,
  payerEnabled: boolean,
): Record<IntakeJourneyStage, IntakeJourneyStageState> {
  const intakeDone = progress.agreementsDone && progress.intakePercent >= 100;
  return {
    agreements: {
      statusLabel: progress.agreementsDone ? 'Services saved' : 'In progress',
      caption: progress.agreementsCaption,
      tone: 'sug',
    },
    intake: !progress.agreementsDone
      ? { statusLabel: 'Locked', lockedReason: LOCKED_AFTER_AGREEMENTS }
      : intakeDone
        ? { statusLabel: 'Complete', caption: 'Everything we needed is in', completed: true }
        : {
            statusLabel: 'In progress',
            caption: `${Math.round(progress.intakePercent)}% complete`,
            tone: 'sug',
          },
    verification: !intakeDone
      ? { statusLabel: 'Locked', lockedReason: 'Opens when your intake is in.' }
      : { statusLabel: 'In review', caption: 'Usually one business day', tone: 'info' },
    payers: payerEnabled
      ? { statusLabel: 'View current status', caption: 'Enrollment with each payer', tone: 'info' }
      : { statusLabel: 'Unavailable', lockedReason: 'Payer connections are not enabled.' },
    import: !progress.agreementsDone
      ? { statusLabel: 'Locked', lockedReason: LOCKED_AFTER_AGREEMENTS }
      : { statusLabel: 'Open', caption: 'Start any time. Runs alongside', tone: 'info' },
  };
}

/** Only presentation availability is known here. Completion remains owned by each live feature. */
export function LiveIntakeJourney({
  enabled = true,
  selectedStage,
  onNavigate,
  intake,
  payers,
  agreements,
  progress,
  payerEnabled,
  onKickoffClick,
}: {
  enabled?: boolean;
  selectedStage: IntakeJourneyStage;
  onNavigate: (stage: IntakeJourneyStage) => void;
  intake: ReactNode;
  payers: ReactNode;
  /** Live Agreements & setup content. Omit for the read-only placeholder (staff embed). */
  agreements?: ReactNode;
  /** Live signals for the lock ladder. Omit to keep every stage open (staff preview). */
  progress?: IntakeJourneyProgress;
  payerEnabled: boolean;
  onKickoffClick?: () => void;
}) {
  return (
    <IntakeJourneyShell
      showJourneyChrome={enabled}
      selectedStage={selectedStage}
      onNavigate={onNavigate}
      stages={
        progress
          ? journeyStagesFromProgress(progress, payerEnabled)
          : {
              agreements: {
                statusLabel: agreements ? 'Available' : 'Coming soon',
                caption: 'Services, agreements, and kickoff',
              },
              intake: { statusLabel: 'Available', caption: 'Practice, providers, and documents' },
              verification: { statusLabel: 'Coming soon', caption: 'JotPsych reviews your intake' },
              payers: payerEnabled
                ? { statusLabel: 'View current status', caption: 'Enrollment with each payer' }
                : {
                    statusLabel: 'Unavailable',
                    lockedReason: 'Payer connections are not enabled.',
                  },
              import: {
                statusLabel: 'Coming soon',
                caption: 'Optional. Runs alongside the others',
              },
            }
      }
      // The live setup rail carries its own kickoff step.
      kickoff={agreements ? undefined : <IntakeKickoffCard onScheduleClick={onKickoffClick} />}
      content={{
        agreements: agreements ?? (
          <IntakeJourneyStatus
            title="Your agreements will appear here"
            statusLabel="DocuSign signing coming soon"
            action={
              <Button variant="contained" onClick={() => onNavigate('intake')}>
                Continue to intake
              </Button>
            }
          >
            <Typography>
              You’ll be able to review and sign your practice agreements here when signing is ready.
              You can continue your intake now.
            </Typography>
          </IntakeJourneyStatus>
        ),
        intake,
        verification: progress ? (
          <IntakeVerificationStatus />
        ) : (
          <IntakeJourneyStatus
            title="Practice review status is coming soon"
            statusLabel="Not available yet"
            action={
              <Button variant="outlined" onClick={() => onNavigate('intake')}>
                Return to intake
              </Button>
            }
          >
            <Typography>
              Your existing intake and review tools remain available. This page does not yet show
              whether your practice has been verified.
            </Typography>
          </IntakeJourneyStatus>
        ),
        // Never instantiate a feature whose own access gate is disabled.
        payers: payerEnabled ? payers : null,
        // Placeholder until the EHR import surface lands in the hub (GDN-3733).
        import: (
          <IntakeJourneyStatus
            title="Import patients"
            statusLabel="Coming soon"
            action={
              <Button variant="outlined" onClick={() => onNavigate('intake')}>
                Return to intake
              </Button>
            }
          >
            <Typography>
              Independent of verification and payers. Start it whenever your export is ready. Your
              first claim needs a patient with insurance on file, so this and payer connections both
              have to finish before you can bill.
            </Typography>
          </IntakeJourneyStatus>
        ),
      }}
    />
  );
}
